import {
  RANKING_FILTER_KEYS,
  decodeFilterValues,
  encodeFilterValues,
  keywordFilter,
  matchesRankingFilters,
  normaliseKeyword,
  type IcpItem,
  type IcpRanked,
  type RankTab,
  type RankingFilterKey,
  type RankingFilters,
  type RankingRow,
} from '@nest-recruit/shared';
import { validatePipelineSearch, type PipelineSearch } from '../pipeline/pipeline-search';
import { lineFiltersOfSearch, matchesLineFilters } from './line-filters';

// F23.10 and F23.11 (ADR 0049): a saved view on RNK-01 is the address's ranking keys, the keywords and the
// filters — as strings, so applying a view and opening a link are the same thing (S32). Pure, so tested.

/**
 * ADR 0050: `rask` and `rorder` keep a view's Jev ask and order; they change nobody's count. ADR 0068: `rline` keeps the line filters,
 * which do. Workflow review D5: `rtab` keeps the Automated ranking tab (ICP match or Jev search) the view was saved on.
 */
const ADDRESS_KEYS = ['tab', 'focus', 'rsort', 'rlayout', 'q', 'rask', 'rorder', 'rtab', 'rline', ...RANKING_FILTER_KEYS] as const;
/** Keywords live in the person's preferences (ADR 0048 Q3); a view carries its own copy under this key. */
const KEYWORDS_KEY = 'kw';
const DEFAULT_TAB: RankTab = 'top_fit';

/** The filters a viewer is offered: Salary only to someone who may see expected salaries (DECISIONS-LOG Group 3 Q3, #5). */
export function rankingFilterKeysFor(canSeeSalary: boolean): readonly RankingFilterKey[] {
  return canSeeSalary ? RANKING_FILTER_KEYS : RANKING_FILTER_KEYS.filter((key) => key !== 'rsal');
}

/** The address's filters, only those offered: a shared link's "Over budget" narrows nothing for someone salaries are hidden from. */
export function filtersOfSearch(search: PipelineSearch, keys: readonly RankingFilterKey[] = RANKING_FILTER_KEYS): RankingFilters {
  const filters: RankingFilters = {};
  for (const key of keys) {
    const values = decodeFilterValues(search[key]);
    if (values.length > 0) filters[key] = values;
  }
  return filters;
}

/** The address's part of a filter change: `undefined` clears a key. */
export function searchPatchOf(filters: RankingFilters): Partial<PipelineSearch> {
  return Object.fromEntries(RANKING_FILTER_KEYS.map((key) => [key, encodeFilterValues(filters[key] ?? [])]));
}

/** What a view saves: the screen as it stands. */
export function viewStateOf(search: PipelineSearch, keywords: readonly string[]): Readonly<Record<string, string>> {
  const state: Record<string, string> = {};
  for (const key of ADDRESS_KEYS) {
    const value = search[key];
    if (typeof value === 'string' && value !== '') state[key] = value;
  }
  const words = encodeFilterValues(keywords.map(normaliseKeyword));
  if (words) state[KEYWORDS_KEY] = words;
  return state;
}

export function keywordsOfView(state: Readonly<Record<string, string>>): readonly string[] {
  return decodeFilterValues(state[KEYWORDS_KEY]);
}

/** The address a view opens: its keys replace the screen's, through the address's own reader (a view is checked like a typed link). */
export function searchOfView(search: PipelineSearch, state: Readonly<Record<string, string>>, viewId: string): PipelineSearch {
  const cleared = Object.fromEntries(ADDRESS_KEYS.map((key) => [key, undefined]));
  const own = Object.fromEntries(ADDRESS_KEYS.flatMap((key) => (state[key] ? [[key, state[key]]] : [])));
  return validatePipelineSearch({ ...search, ...cleared, ...own, view: viewId });
}

/** The screen still matches the view: same keys and values, keywords in any order and any case. */
export function isSameView(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (key === KEYWORDS_KEY) {
      const left = new Set(keywordsOfView(a).map((word) => word.toLowerCase()));
      const right = keywordsOfView(b).map((word) => word.toLowerCase());
      if (left.size !== right.length || right.some((word) => !left.has(word))) return false;
      continue;
    }
    if (key === 'tab' ? (a[key] ?? DEFAULT_TAB) !== (b[key] ?? DEFAULT_TAB) : (a[key] ?? '') !== (b[key] ?? '')) return false;
  }
  return true;
}

/**
 * How many people a view shows: its tab, its name search, its filters, and every keyword — a CV that can't
 * be searched stays, as on the screen (ADR 0048 Q2). Null while one of its keywords is still being looked for.
 * ADR 0068: with the profile's lines given, a view's line filters count too.
 */
export function viewCount(
  rows: readonly RankingRow[],
  state: Readonly<Record<string, string>>,
  matches: ReadonlyMap<string, ReadonlySet<string>>,
  keys: readonly RankingFilterKey[] = RANKING_FILTER_KEYS,
  lines?: { readonly items: readonly IcpItem[]; readonly ranks: ReadonlyMap<string, IcpRanked> | undefined },
): number | null {
  const sets: ReadonlySet<string>[] = [];
  for (const word of keywordsOfView(state)) {
    const set = matches.get(word.toLowerCase());
    if (!set) return null;
    sets.push(set);
  }
  const tab = state.tab ?? DEFAULT_TAB;
  const query = (state.q ?? '').trim().toLowerCase();
  const filters = filtersOfSearch(validatePipelineSearch(state), keys);
  const lineFilters = lines ? lineFiltersOfSearch(state.rline, lines.items) : {};
  const isLine = (row: RankingRow): boolean => lines === undefined || matchesLineFilters(lines.ranks?.get(row.applicationId), lines.items, lineFilters);
  return keywordFilter(rows, sets).filter((row) => row.tab === tab && (query === '' || row.name.toLowerCase().includes(query)) && matchesRankingFilters(row, filters) && isLine(row)).length;
}
